import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { EVENTO_ABRIR_COOKIES, lerConsentimento, salvarConsentimento } from '../lib/consentimento';

/**
 * O banner de cookies (LGPD).
 *
 * Texto e opções são os que o cliente mandou: aceitar tudo, rejeitar os não
 * essenciais ou personalizar por categoria. Aparece para quem ainda não
 * escolheu (ou cuja escolha passou do prazo) e volta pelo link "Preferências de
 * cookies" do rodapé — revogar precisa ser tão fácil quanto aceitar.
 *
 * "Rejeitar" tem o mesmo peso visual de "Aceitar": esconder a recusa num link
 * apagado é o que a ANPD aponta como consentimento viciado.
 */
export function BannerCookies() {
  const [aberto, setAberto] = useState(() => lerConsentimento() === null);
  const [personalizar, setPersonalizar] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(true);
  const primeiroBotao = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const abrir = () => {
      // Reabrindo: as caixas mostram a escolha atual, não o padrão.
      const atual = lerConsentimento();
      setAnalytics(atual?.analytics ?? true);
      setMarketing(atual?.marketing ?? true);
      setPersonalizar(Boolean(atual));
      setAberto(true);
    };
    window.addEventListener(EVENTO_ABRIR_COOKIES, abrir);
    return () => window.removeEventListener(EVENTO_ABRIR_COOKIES, abrir);
  }, []);

  useEffect(() => {
    if (aberto) primeiroBotao.current?.focus({ preventScroll: true });
  }, [aberto]);

  if (!aberto) return null;

  const decidir = (a: boolean, m: boolean) => {
    salvarConsentimento(a, m);
    setAberto(false);
    setPersonalizar(false);
  };

  return (
    <section className="cookies" role="dialog" aria-modal="false" aria-labelledby="cookies-titulo">
      <div className="cookies-inner">
        <div className="cookies-texto">
          <h2 id="cookies-titulo">Cookies e privacidade</h2>
          <p>
            Usamos cookies para o funcionamento do site e, com o seu consentimento, para medir audiência (Google
            Analytics) e personalizar anúncios (Google Ads, Meta/Facebook). Você pode aceitar, recusar ou personalizar.
            Saiba mais na nossa{' '}
            <Link to="/politica-de-privacidade" target="_blank" rel="noopener">
              Política de Privacidade
            </Link>
            .
          </p>
        </div>

        <div className="cookies-acoes">
          <button
            ref={primeiroBotao}
            type="button"
            className="cookies-link"
            aria-expanded={personalizar}
            aria-controls="cookies-categorias"
            onClick={() => setPersonalizar((v) => !v)}
          >
            Personalizar
          </button>
          <button type="button" className="btn ghost" onClick={() => decidir(false, false)}>
            Rejeitar não essenciais
          </button>
          <button type="button" className="btn primary" onClick={() => decidir(true, true)}>
            Aceitar todos
          </button>
        </div>
      </div>

      {personalizar && (
        <div className="cookies-categorias" id="cookies-categorias">
          <Categoria
            id="cookies-necessarios"
            titulo="Necessários"
            texto="Essenciais para o site funcionar (sacola, login, segurança). Sempre ativos."
            marcado
            travado
          />
          <Categoria
            id="cookies-analiticos"
            titulo="Analíticos"
            texto="Google Analytics — nos ajuda a entender como o site é usado."
            marcado={analytics}
            onMudar={setAnalytics}
          />
          <Categoria
            id="cookies-marketing"
            titulo="Marketing"
            texto="Meta Pixel e Google Ads — usados para anúncios personalizados."
            marcado={marketing}
            onMudar={setMarketing}
          />
          <div className="cookies-salvar">
            <button type="button" className="btn primary" onClick={() => decidir(analytics, marketing)}>
              Salvar preferências
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function Categoria({
  id,
  titulo,
  texto,
  marcado,
  travado,
  onMudar,
}: {
  id: string;
  titulo: string;
  texto: string;
  marcado: boolean;
  travado?: boolean;
  onMudar?: (v: boolean) => void;
}) {
  return (
    <label className={`cookies-categoria${travado ? ' travada' : ''}`} htmlFor={id}>
      <span>
        <b>{titulo}</b>
        <small>{texto}</small>
      </span>
      <input
        id={id}
        type="checkbox"
        className="cookies-chave"
        checked={marcado}
        disabled={travado}
        onChange={(e) => onMudar?.(e.target.checked)}
      />
    </label>
  );
}
